import { Injectable } from '@angular/core';

interface LocalStorageItemI {
  value: any;
  permanent: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class LocalStorageService {

  constructor() { }

  public setItem(key: string, value: any, permanent: boolean=false): void {
    const item: LocalStorageItemI = {
      value: value,
      permanent: permanent
    };
    localStorage.setItem(key, JSON.stringify(item));
  }

  public getItem(key: string): any {
    return this.readItem(key)?.value ?? null;
  }

  public isPermanent(key: string): boolean {
    return this.readItem(key)?.permanent ?? false;
  }

  /**
   * Read an entry, or null when it is missing or was not written by this service.
   *
   * localStorage is shared with Firebase Auth, ngx-translate and anything else on the origin, and
   * `JSON.parse` throws on their values — so every read goes through here rather than parsing
   * inline.
   */
  private readItem(key: string): LocalStorageItemI | null {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return null;
      const item = JSON.parse(raw);
      return item && typeof item === 'object' && 'value' in item ? item as LocalStorageItemI : null;
    } catch {
      return null;
    }
  }

  /**
   * Drop non-permanent entries, e.g. on logout.
   *
   * Iterates a snapshot of the keys: `localStorage.key(i)` reindexes on every removal, so walking
   * a live index skips one entry per deletion. Entries this service did not write are left alone —
   * clearing Firebase Auth's own keys from here would sign the user out sideways.
   *
   * That exemption is by shape, not by name: anything stored without this service's
   * `{ value, permanent }` envelope is invisible here, `LoginComponent`'s raw `currentUserData`
   * included. A key that must be dropped on logout has to be written through `setItem`.
   *
   * `permanent` is a consent guarantee, not a convenience: the cookie choice is stored with it and
   * must survive logout, or the banner reappears on every sign-out and a refusal is discarded.
   */
  public clear(clearPermanentItems: boolean=false): void {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      const item = this.readItem(key);
      if (item === null) continue;
      if (item.permanent && !clearPermanentItems) continue;
      localStorage.removeItem(key);
    }
  }

  public removeItem(key: string): void {
    localStorage.removeItem(key);
  }
}
